/**
 * Outros planos para o mesmo cliente: categorias deste grupo e de outros
 * grupos do mesmo segmento, com crédito parecido, ordenadas pelo que o
 * cliente ganha frente ao financiamento. Conta fixa, sem IA — o mesmo número
 * que vai para o estudo (decisão do Rick, 17/10: mostrar alternativa quando o
 * plano escolhido perde por prazo ou taxa).
 *
 * As categorias e grupos são lidos por quem chama (web: alternativasDados.ts;
 * North IA: chat-ai/apresentacao.ts); aqui só a conta.
 */
import { computeSimulacao } from './simulacao.ts';
import { jurosPctDoCredito } from './empate.ts';
import type { Segmento } from './comparativo.ts';
import type { Alternativa, Candidato, PayloadProposta } from './tipos.ts';

export type { Alternativa, Candidato };

/** Crédito por cota até 15% para mais ou para menos. */
export const FOLGA_CREDITO_PCT = 15;

/**
 * Identifica a alternativa na seleção do vendedor. A mesma categoria de um
 * grupo pode vir com prazos e taxas diferentes (1572 H1: 180, 210 e 234
 * meses), então grupo e categoria não bastam.
 */
export const chaveAlternativa = (a: Pick<Candidato, 'grupo' | 'categoria' | 'prazo' | 'taxaAdminPct'>) =>
  `${a.grupo}|${a.categoria}|${a.prazo}|${a.taxaAdminPct}`;

export type OrdemAlternativas = 'economia' | 'taxa' | 'prazo';

export const NOME_ORDEM: Record<OrdemAlternativas, string> = {
  economia: 'Maior economia',
  taxa: 'Menor taxa',
  prazo: 'Menor prazo',
};

/**
 * Uma alternativa por grupo × prazo × taxa (a categoria de crédito mais
 * próximo), com a economia frente ao financiamento do segmento.
 */
export const ranquearAlternativas = (
  candidatos: Candidato[],
  o: {
    creditoPorCota: number;
    cotas: number;
    jurosAa: number;
    sistema: 'SAC' | 'Price';
    grupoAtual: number | null;
    categoriaAtual: string | null;
    /** Prazo do plano do estudo, para achar a linha certa da categoria. */
    prazoAtual: number | null;
    ordem: OrdemAlternativas;
  },
): Alternativa[] => {
  const ehAtual = (c: Candidato) =>
    c.grupo === o.grupoAtual && c.categoria === o.categoriaAtual && (o.prazoAtual === null || c.prazo === o.prazoAtual);
  const distancia = (c: Candidato) => Math.abs(c.credito - o.creditoPorCota);
  const melhores = new Map<string, Candidato>();
  for (const c of candidatos) {
    if (!(c.credito > 0 && c.prazo > 0 && c.taxaAdminPct > 0)) continue;
    const chave = `${c.grupo}|${c.prazo}|${c.taxaAdminPct}`;
    const ja = melhores.get(chave);
    // A categoria da proposta fica sempre; nas outras, a de crédito mais próximo.
    if (!ja || (!ehAtual(ja) && (ehAtual(c) || distancia(c) < distancia(ja)))) melhores.set(chave, c);
  }
  const lista: Alternativa[] = [...melhores.values()].map((c) => {
    const economiaPct = jurosPctDoCredito(c.prazo, o.jurosAa, o.sistema) - c.taxaAdminPct;
    return {
      ...c,
      mesmoGrupo: c.grupo === o.grupoAtual,
      atual: ehAtual(c),
      economiaPct,
      economia: (c.credito * o.cotas * economiaPct) / 100,
    };
  });
  const porOrdem: Record<OrdemAlternativas, (a: Alternativa, b: Alternativa) => number> = {
    economia: (a, b) => b.economia - a.economia,
    taxa: (a, b) => a.taxaAdminPct - b.taxaAdminPct || a.prazo - b.prazo,
    // Os que já ganham primeiro, do prazo mais curto; os que perdem por último.
    prazo: (a, b) => Number(b.economia > 0) - Number(a.economia > 0) || a.prazo - b.prazo,
  };
  return lista.sort((a, b) => porOrdem[o.ordem](a, b) || a.grupo - b.grupo);
};

/** Parâmetros do grupo que a simulação usa (servopa_group_details). */
export type DetalhesGrupo = {
  /** % do lance fixo do grupo (coluna lance_embutido). */
  lanceFixoPct: number;
  tipoSeguro: string | null;
  taxaSeguro: number | null;
};

const num = (v: unknown, padrao = 0) => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : padrao;
};

/**
 * A proposta refeita no plano alternativo: mesmo cliente, mesma junção de
 * cotas, mesmos lances (livre, embutido e fidelidade em %) e mês de
 * contemplação, com crédito, prazo, taxa e parcela da outra categoria e as
 * regras do outro grupo — a mesma conta do simulador.
 */
export const payloadDaAlternativa = <P extends PayloadProposta>(
  original: P,
  alt: Candidato,
  detalhes: DetalhesGrupo,
  segmento: Segmento,
): P & PayloadProposta => {
  const cotas = parseInt(String(original.juncaoCota ?? '1'), 10) || 1;
  const tipo = (detalhes.tipoSeguro ?? '').toUpperCase();
  const obrigatorio = tipo === 'SEGURO OBRIGATORIO';
  const seguro = obrigatorio || String(original.seguro ?? '').toLowerCase() === 'sim' ? 'Sim' : 'Não';
  const mes = Math.min(Math.max(1, Math.round(num(original.mesesContemplacao, 12))), alt.prazo);
  const desconto = alt.flex ? num(original.descontoFlex) : 0;
  const sim = computeSimulacao({
    valorCredito: alt.credito,
    parcelaSemSeguro: alt.parcela,
    valorSeguro: alt.valorSeguro,
    prazo: alt.prazo,
    taxaAdmin: alt.taxaAdminPct,
    mesesContemplacao: mes,
    juncaoCota: String(cotas),
    lanceLivre: String(num(original.lanceLivre)),
    lanceEmbutido: String(num(original.lanceEmbutido)),
    fixedLancePercent: detalhes.lanceFixoPct,
    percLanceFidelidade: num(original.lanceFidelidadePercent),
    descontoFlex: String(desconto),
    isFlex: alt.flex,
    isAuto: segmento === 'veiculo',
    seguroEfetivo: seguro,
    seguroForcadoPos: obrigatorio || tipo === 'SEGURO NA CONTEMPLACAO',
    taxaSeguro: detalhes.taxaSeguro ?? 0,
  });
  return {
    ...original,
    groupNumber: alt.grupo,
    categoria: alt.categoria,
    prazo: alt.prazo,
    taxaAdmin: alt.taxaAdminPct,
    parcelaSemSeguro: alt.parcela,
    valorSeguro: alt.valorSeguro,
    seguro,
    descontoFlex: String(desconto),
    mesesContemplacao: mes,
    ...sim,
    valorCredito: sim.valorCreditoJunto,
  } as P & PayloadProposta;
};

// ── Leitura (o banco é lido por quem chama; aqui só a forma) ─────────────────

/** Faixa de crédito por cota que entra na busca (FOLGA_CREDITO_PCT para cada lado). */
export const faixaDeCredito = (creditoPorCota: number) => ({
  min: Math.floor(creditoPorCota * (1 - FOLGA_CREDITO_PCT / 100)),
  max: Math.ceil(creditoPorCota * (1 + FOLGA_CREDITO_PCT / 100)),
});

/** Colunas lidas de servopa_group_categories e servopa_groups_enriched. */
export const COLUNAS_CATEGORIA = 'id, group_number, categoria_code, prazo, valor_credito, admin_fee_pct, parcela_sem_seguro, valor_seguro';
export const COLUNAS_GRUPO = 'group_number, segment, flex, vacancies';
export const codigoDoSegmento = (segmento: Segmento) => (segmento === 'imovel' ? 'I' : 'M');

const numLido = (v: unknown) => {
  const n = typeof v === 'number' ? v : Number(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

/** "1.111", "58 vagas", 12 → número de vagas. */
export const vagasDe = (v: unknown): number => {
  if (typeof v === 'number') return Number.isFinite(v) ? Math.max(0, Math.trunc(v)) : 0;
  const digitos = (String(v ?? '').match(/\d[\d.,]*/)?.[0] ?? '').replace(/\D/g, '');
  return digitos ? Number(digitos) : 0;
};

/**
 * Linhas do banco → candidatos. Só grupos do segmento com vaga, mais o grupo
 * da proposta mesmo sem vaga, para ele aparecer como "este plano".
 */
export const candidatosDasLinhas = (
  categorias: Record<string, unknown>[],
  grupos: Record<string, unknown>[],
  grupoAtual: number | null,
): Candidato[] => {
  const porGrupo = new Map(
    grupos.map((g) => [Number(g.group_number), { flex: g.flex === true, vagas: vagasDe(g.vacancies) }]),
  );
  return categorias.flatMap((c) => {
    const grupo = Number(c.group_number);
    const g = porGrupo.get(grupo);
    if (!g || (g.vagas <= 0 && grupo !== grupoAtual)) return [];
    return [
      {
        grupo,
        categoria: String(c.categoria_code ?? ''),
        credito: numLido(c.valor_credito),
        prazo: numLido(c.prazo),
        taxaAdminPct: numLido(c.admin_fee_pct),
        parcela: numLido(c.parcela_sem_seguro),
        valorSeguro: numLido(c.valor_seguro),
        flex: g.flex,
        vagas: g.vagas,
      },
    ];
  });
};

// ── Seleção com que a Apresentação abre ──────────────────────────────────────

/** Quantos outros planos já entram marcados no estudo. */
export const PRE_SELECIONADOS = 3;

/**
 * Na primeira lista: o plano do estudo e os melhores que ganham do
 * financiamento. Se o plano da proposta perde, o que o cliente quer é o
 * prazo mais curto que já ganha — a lista abre nessa ordem.
 */
export const selecaoInicial = (alternativas: Alternativa[]): { ordem: OrdemAlternativas | null; chaves: Set<string> } => {
  const atual = alternativas.filter((a) => a.atual);
  const perde = atual.some((a) => a.economia < 0);
  const melhores = alternativas
    .filter((a) => !a.atual && a.economia > 0)
    .sort(perde ? (a, b) => a.prazo - b.prazo : (a, b) => b.economia - a.economia)
    .slice(0, PRE_SELECIONADOS);
  return { ordem: perde ? 'prazo' : null, chaves: new Set([...atual, ...melhores].map(chaveAlternativa)) };
};

/** Parâmetros do ranking que saem da proposta e do estudo (tudo menos a ordem). */
export type ContextoAlternativas = Omit<Parameters<typeof ranquearAlternativas>[1], 'ordem'>;

/**
 * Os outros planos com que a Apresentação abre, sem o vendedor mexer: a
 * seleção inicial, na ordem em que a caixinha mostra. É o que o botão grava
 * quando o vendedor gera o link sem mudar nada.
 */
export const alternativasPadrao = (candidatos: Candidato[], o: ContextoAlternativas): Alternativa[] => {
  const { ordem, chaves } = selecaoInicial(ranquearAlternativas(candidatos, { ...o, ordem: 'economia' }));
  return ranquearAlternativas(candidatos, { ...o, ordem: ordem ?? 'economia' }).filter((a) => chaves.has(chaveAlternativa(a)));
};
